/**
 * AtlasExchangeOfficeModule — the player face of the Exchange Office (W7).
 *
 * Press U near a settlement: the office window lists the town's surplus goods
 * with BUY prices (window ceiling) and your commodity crates with SELL prices
 * (window floor) — the spread is the treasury's margin, and both float with
 * the office vault exactly as the server computes them (the price mirror
 * below MUST stay aligned with reducers/exchange.ts; display only — the
 * server is the authority on every number).
 *
 * Goods-only bridge: SOV ↔ commodity crates. No labor hiring in v1.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { sfx } from '../lib/sfx.js';

// ── Server price mirror (reducers/exchange.ts — keep aligned) ────────────────
const SOV_PER_COIN = 2;
const VAULT_TARGET_SOV = 500;
function priceWindowSov(baseCoin: number, vaultSov: bigint): { floorSov: number; ceilSov: number } {
  const f = Math.max(0.25, Math.min(2, Number(vaultSov) / VAULT_TARGET_SOV));
  const base = baseCoin * SOV_PER_COIN;
  return {
    floorSov: Math.max(1, Math.round(base * 0.8 * Math.min(1, f))),
    ceilSov: Math.max(2, Math.round(base * 1.25 * Math.max(1, 1.5 - f / 2))),
  };
}
const BASE_VALUES: Record<string, number> = {
  grain: 3, fish: 4, ore: 5, stone: 4, crystal: 9, resin: 5, fiber: 4, salt: 6,
  flour: 12, ingot: 16, cut_crystal: 25, cloth: 14,
  bread: 10, preserved_fish: 11, tools: 28, garment: 21,
  wine: 18, jewelry: 52, festival_goods: 27,
};

interface EconRow { settlementId: bigint; centerX: number; centerZ: number }
interface StockRow { settlementId: bigint; commodity: string; qty: number }
interface RoundRow { settlementId: bigint; commodity: string; avgPrice: number; epoch: number }
interface OfficeRow { settlementId: bigint; officeHex: string; dailyInSov: bigint; dailyOutSov: bigint }
interface WalletRow { identity: { toHexString(): string; isEqual(o: unknown): boolean }; sov: bigint }
interface ItemRow { itemId: bigint; owner: { isEqual(o: unknown): boolean }; assetId: bigint }
interface AssetRow { assetId: bigint; kind: string; contentRef: string }
interface Reducers {
  officeBuyGoods(a: { settlementId: bigint; commodity: string; qty: number }): Promise<void>;
  officeSellGoods(a: { settlementId: bigint; itemId: bigint }): Promise<void>;
}

export class AtlasExchangeOfficeModule implements IModule {
  readonly name = 'AtlasExchangeOffice';
  private win: UiWindow | null = null;
  private body: HTMLDivElement | null = null;
  private note: HTMLDivElement | null = null;
  private timer: number | null = null;
  private subscribedSid: bigint | null = null;

  init(_app: App): void {
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'KeyU' || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.currentWorldId === null) return;
      if (atlas.spaceFlight) return; // in the sector U is the utility beam (space campaign B — pictured: the office opened over the chart)
      this.toggle();
    });
  }

  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }

  private nearestSid(): bigint | null {
    let best: bigint | null = null;
    let bestD = 600; // must actually be near a town to trade
    for (const e of this.tbl<EconRow>('settlementEcon')) {
      const d = Math.hypot(e.centerX - atlas.localX, e.centerZ - atlas.localZ);
      if (d < bestD) { best = e.settlementId; bestD = d; }
    }
    return best;
  }

  private sidHandle: { unsubscribe(): void } | null = null;
  private walletSubbed = false;
  private ensureSubs(sid: bigint): void {
    if (this.subscribedSid === sid) return;
    this.subscribedSid = sid;
    // (bug sweep) release the previous town's queries; the un-scoped wallet
    // table subscribes ONCE — per-town stacking had no release path.
    try { this.sidHandle?.unsubscribe(); } catch { /* gone */ }
    this.sidHandle = null;
    try {
      this.sidHandle = atlas.conn?.subscriptionBuilder().subscribe([
        `SELECT * FROM settlement_stock WHERE settlement_id = ${sid}`,
        `SELECT * FROM market_round WHERE settlement_id = ${sid}`,
        `SELECT * FROM exchange_office WHERE settlement_id = ${sid}`,
        `SELECT * FROM bridge_audit WHERE settlement_id = ${sid}`,
        // The planetary port (2026-09-17): the town's port row, its two books, its runs and its surface warehouse.
        `SELECT * FROM planetary_port WHERE settlement_id = ${sid}`,
        `SELECT * FROM function_ledger WHERE settlement_id = ${sid}`,
        'SELECT * FROM port_haul', "SELECT * FROM cargo_lot WHERE loc_kind = 'port'",
      ]) as { unsubscribe(): void } | null;
      if (!this.walletSubbed) {
        this.walletSubbed = true;
        atlas.conn?.subscriptionBuilder().subscribe(['SELECT * FROM wallet']);
      }
    } catch { /* pre-publish server */ }
  }

  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    const sid = this.nearestSid();
    if (sid === null) return; // silent: no office out in the wilds
    this.ensureSubs(sid);
    if (!this.win) {
      this.win = makeWindow({
        id: 'exchange-office', title: 'Exchange Office', icon: 'market',
        width: 460, height: 520, minWidth: 380, minHeight: 320, x: 80, y: 80,
      });
      const p = document.createElement('div');
      p.style.cssText = 'display:flex;flex-direction:column;height:100%;color:var(--ax-text);font:12px/1.5 var(--ax-font)';
      this.note = document.createElement('div');
      this.note.style.cssText = 'padding:6px 10px;border-bottom:1px solid var(--ax-line);color:var(--ax-soft);min-height:16px';
      p.appendChild(this.note);
      this.body = document.createElement('div');
      this.body.style.cssText = 'flex:1;overflow:auto;padding:8px 10px';
      p.appendChild(this.body);
      this.win.body.appendChild(p);
    }
    this.win.setOpen(true);
    if (this.timer === null) {
      this.timer = window.setInterval(() => { if (this.win?.isOpen()) this.render(); else if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; } }, 1000);
    }
    this.render();
  }

  /** Escape for innerHTML interpolation (defense in depth — commodity keys are
   *  server-config charset [a-z_], but templates must not trust that forever). */
  private esc(v: string): string {
    return v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }

  private say(text: string, err = false): void {
    if (this.note) { this.note.textContent = text; this.note.style.color = err ? 'var(--ax-bad)' : 'var(--ax-soft)'; }
  }

  private render(): void {
    const b = this.body;
    const conn = atlas.conn;
    if (!b || !conn) return;
    const sid = this.nearestSid();
    if (sid === null) { b.innerHTML = '<div style="opacity:.7">No settlement in range — offices live in towns.</div>'; return; }
    this.ensureSubs(sid);

    const office = this.tbl<OfficeRow>('exchangeOffice').find((o) => o.settlementId === sid) ?? null;
    const vault = office
      ? this.tbl<WalletRow>('wallet').find((w) => w.identity.toHexString() === office.officeHex)?.sov ?? 0n
      : 0n;
    const rounds = new Map<string, { avgPrice: number; epoch: number }>();
    for (const r of this.tbl<RoundRow>('marketRound')) {
      if (r.settlementId !== sid) continue;
      const hit = rounds.get(r.commodity);
      if (!hit || r.epoch > hit.epoch) rounds.set(r.commodity, { avgPrice: r.avgPrice, epoch: r.epoch });
    }
    const obs = (c: string): number => rounds.get(c)?.avgPrice ?? BASE_VALUES[c] ?? 4;

    const stocks = this.tbl<StockRow>('settlementStock')
      .filter((s) => s.settlementId === sid && s.qty >= 1)
      .sort((x, y) => y.qty - x.qty)
      .slice(0, 8);
    const reducers = conn.reducers as unknown as Reducers;

    const rows: string[] = [];
    rows.push('<div style="font-weight:600;margin-bottom:6px">Town surplus — buy in crates</div>');
    for (const s of stocks) {
      const w = priceWindowSov(obs(s.commodity), vault);
      rows.push(
        `<div class="xo-row" style="display:flex;align-items:center;gap:8px;padding:3px 0;border-bottom:1px solid var(--ax-line)">` +
        `<span style="flex:1">${this.esc(s.commodity).replace(/_/g, ' ')}</span>` +
        `<span style="opacity:.75">×${Math.floor(s.qty)}</span>` +
        `<button data-buy="${this.esc(s.commodity)}" style="padding:2px 10px">Buy 1 · ${w.ceilSov} SOV</button></div>`,
      );
    }
    rows.push('<div style="font-weight:600;margin:12px 0 6px">Your crates — sell at the window floor</div>');
    const assets = new Map(this.tbl<AssetRow>('asset').map((a) => [a.assetId.toString(), a]));
    const me = conn.identity;
    let crates = 0;
    for (const it of this.tbl<ItemRow>('inventoryItem')) {
      if (!me || !it.owner.isEqual(me)) continue;
      const a = assets.get(it.assetId.toString());
      if (!a || a.kind !== 'commodity') continue;
      const m = /^bridge:([a-z_]+):(\d+)$/.exec(a.contentRef);
      if (!m) continue;
      const w = priceWindowSov(obs(m[1]), vault);
      crates++;
      rows.push(
        `<div style="display:flex;align-items:center;gap:8px;padding:3px 0;border-bottom:1px solid var(--ax-line)">` +
        `<span style="flex:1">${this.esc(m[1]).replace(/_/g, ' ')} ×${Number(m[2])}</span>` +
        `<button data-sell="${it.itemId}" style="padding:2px 10px">Sell · ${w.floorSov * Number(m[2])} SOV</button></div>`,
      );
    }
    if (crates === 0) rows.push('<div style="opacity:.6">No crates yet — buy the town surplus above.</div>');
    // The planetary port (2026-09-17): the town's own bridge to space, read straight from its rows.
    {
      interface PortRow { portId: bigint; settlementId: bigint; state: string; generation: number; exported: number; lifetimeCapU: number; lastError: string }
      interface LedgerRow { settlementId: bigint; fn: string; cash: bigint }
      interface LotRow { locKind: string; locId: string; familyKey: string; qty: number; tag: string }
      interface HaulRow { portId: bigint; direction: string; qty: number; rewardSov: bigint; state: string; stationKey: string }
      const port = this.tbl<PortRow>('planetaryPort').find((p) => p.settlementId === sid) ?? null;
      if (port) {
        const book = this.tbl<LedgerRow>('functionLedger').find((f) => f.settlementId === sid && f.fn === 'orbital_trade')?.cash ?? 0n;
        let pool = 0;
        for (const l of this.tbl<LotRow>('cargoLot')) if (l.locKind === 'port' && l.locId === `port:${port.portId}` && l.tag === '') pool += l.qty;
        const run = this.tbl<HaulRow>('portHaul').find((h) => h.portId === port.portId && (h.state === 'offered' || h.state === 'aboard')) ?? null;
        const runLine = run
          ? `${run.direction === 'up' ? 'a run UP to' : 'a run DOWN from'} ${this.esc(run.stationKey)} · ${run.qty} u ore · pays ${run.rewardSov} ⓒ · ${run.state}${run.state === 'offered' && run.direction === 'up' ? ' — H at the spaceport' : ''}`
          : 'no run on offer';
        rows.push(
          '<div style="font-weight:600;margin:12px 0 6px">Planetary port — this town trades ore with its civ\'s yard</div>' +
          `<div style="opacity:.8;line-height:1.6">${this.esc(port.state)} · generation ${port.generation} · exported ${port.exported}/${port.lifetimeCapU} u` +
          ` · book ${book} coins · warehouse ${pool} u ore waiting for the market<br>${runLine}` +
          `${port.lastError ? `<br><span style="color:var(--ax-warn)">${this.esc(port.lastError)}</span>` : ''}</div>`,
        );
      }
    }
    rows.push(`<div style="margin-top:10px;opacity:.6">vault ${vault} SOV · prices float with it · daily cap 200 SOV per office</div>`);
    b.innerHTML = rows.join('');

    for (const btn of Array.from(b.querySelectorAll('[data-buy]'))) {
      btn.addEventListener('click', () => {
        const commodity = (btn as HTMLElement).dataset.buy!;
        this.say(`buying ${commodity}…`);
        void reducers.officeBuyGoods({ settlementId: sid, commodity, qty: 1 })
          .then(() => { sfx('sfx_coin_purchase', { gain: 0.35 }); this.say(`bought a crate of ${commodity.replace(/_/g, ' ')}`); })
          .catch((err: Error) => this.say(String(err.message ?? err), true));
      });
    }
    for (const btn of Array.from(b.querySelectorAll('[data-sell]'))) {
      btn.addEventListener('click', () => {
        const itemId = BigInt((btn as HTMLElement).dataset.sell!);
        this.say('selling…');
        void reducers.officeSellGoods({ settlementId: sid, itemId })
          .then(() => { sfx('sfx_coin_sell', { gain: 0.35 }); this.say('sold — SOV credited'); })
          .catch((err: Error) => this.say(String(err.message ?? err), true));
      });
    }
  }
}
